import { useMemo } from 'react'

import { DynamicFormFieldConfigsDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { CapabilityEnum, CapabilityFilterRule } from 'modules/dynamic_forms/types'

const EMPTY_COUNTRIES: string[] = []

interface UseDynamicFormFieldConfigsOptions {
  /** The capability to filter schema fields by (e.g. 'employee_creation', 'dynamic_forms'). */
  capability: CapabilityEnum
  /** Country codes to filter schema fields by (via capability rules). */
  countries?: string[]
  /**
   * Additional capability filter rules beyond the default `country` rule.
   * Each rule is matched against sub-keys of the capability in the YAML schema.
   *
   * @example
   * ```ts
   * // Only fields with `form: [elc_onboarding]` under the capability
   * rules: [{ key: 'form', values: ['elc_onboarding'], operation: 'include' }]
   * ```
   */
  rules?: CapabilityFilterRule[]
  /** Whether to skip the query. */
  skip?: boolean
}

/**
 * Fetches dynamic form field configurations from the data dispatcher.
 *
 * Uses the existing `dataSchemasConnection` query filtered by a consumer-provided capability.
 * The consumer decides which capability to fetch, making this hook generic and reusable.
 *
 * @example
 * ```tsx
 * const { fields } = useDynamicFormFieldConfigs({ capability: 'employee_creation' })
 * const { fields } = useDynamicFormFieldConfigs({ capability: 'dynamic_forms', countries: ['es'] })
 * ```
 */
export function useDynamicFormFieldConfigs({
  capability,
  countries,
  rules: extraRules,
  skip = false,
}: UseDynamicFormFieldConfigsOptions) {
  const capabilityFilter = useMemo(
    () => ({
      capability,
      rules: [
        { key: 'country', values: countries ?? EMPTY_COUNTRIES, operation: 'none_or_any' as const },
        ...(extraRules ?? []),
      ],
    }),
    [capability, countries, extraRules]
  )

  const { data, loading, error } = useQuery(DynamicFormFieldConfigsDocument, {
    variables: { capabilityFilter },
    skip,
  })

  const fields = useMemo(() => data?.dataDispatcher?.dataSchemasConnection?.nodes ?? [], [data])

  return { fields, loading, error }
}
